/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { MarkerF, OverlayViewF, useGoogleMap } from "@react-google-maps/api";
import { AwvInfo, AwvInfoConditionEnum, AwvInfos } from "client";
import { FC, useState, useCallback, useRef, useEffect } from "react";
import { ReactComponent as HighLightIcon } from "assets/icon_highlight.svg";
import { Const } from "const";
import { Magnify, MagnifyProps } from "./Magnify";
import {
  EdgeNode,
  LandmarkNode,
  NamedNode,
  NormalEdgePolygon,
  RoadClosureNode,
} from "./NodeMapOverlay";
import { useMapDataContext } from "context/MapDataProvider";
import { createAwvIconDataUrl } from "./AwvIcon";
import { MyMarkerClustererF } from "./MyMarkerClusterF";
import { useTheme } from "@mui/material";
import {
  ClusterIconStyle,
  Clusterer,
  TCalculator,
} from "@react-google-maps/marker-clusterer";
import { useDebounce } from "react-use";

const awvIconImageSize = 100;
const awvIconVehicleLength = 74;
const awvIconRearWheelPos = 70;

export interface AwvMapOverlayProps {
  awvInfos?: AwvInfos;
  heading?: number;
  onAwvSelect?: (awv: AwvInfo) => void;
  selected?: string[];
  isMagnify?: boolean;
}

export const AwvMapOverlay: FC<AwvMapOverlayProps> = (props) => {
  const theme = useTheme();
  const { mapData } = useMapDataContext();
  const [magnify, setMagnify] = useState<MagnifyProps | null>(null);
  const { awvInfos, heading = 0, onAwvSelect, selected, isMagnify } = props;
  const map = useGoogleMap();
  const [markerClusterer, setMarkerClusterer] = useState<Clusterer>();
  const [currentHeading, setCurrentHeading] = useState(heading);
  const calculator: TCalculator = useCallback(
    (markers: google.maps.Marker[], num: number) => {
      const maxMarker = markers.reduce(
        (prev, cur) =>
          (prev?.getZIndex() ?? 0) > (cur.getZIndex() ?? 0) ? prev : cur,
        markers[0]
      );
      const label = maxMarker.getLabel();
      let name = label?.toString();
      if (label && typeof label === "object" && "text" in label) {
        name = label.text;
      }
      const index =
        awvInfos?.awvInfos?.findIndex((awv) => awv.nameId === name) ?? -1;
      return {
        text: name ?? "",
        index: index + 2, // index is 1 origin.
      };
    },
    [awvInfos]
  );
  const [mapLoaded, setMapLoaded] = useState(false);
  useDebounce(
    () => {
      if (map) {
        // add heading_changed event handler.
        map.addListener("heading_changed", () => {
          setCurrentHeading(map.getHeading() ?? 0);
        });
      }
    },
    500,
    [map]
  );
  useEffect(() => {
    if (map) {
      setMapLoaded(true);
    }
  }, [map]);

  useEffect(() => {
    if (markerClusterer) {
      const defaultIconStyle: ClusterIconStyle = {
        url: createAwvIconDataUrl(),
        width: 100,
        height: 100,
        textSize: 24,
        fontWeight: "normal",
        fontStyle: theme.typography.h5.fontStyle,
        fontFamily: theme.typography.fontFamily,
      };
      const clusterIconStyles: ClusterIconStyle[] =
        awvInfos?.awvInfos?.map((awv) => {
          return {
            ...defaultIconStyle,
            url: createAwvIconDataUrl(awv, currentHeading),
          };
        }) ?? [];
      clusterIconStyles.unshift(defaultIconStyle);
      markerClusterer.setStyles(clusterIconStyles);
      markerClusterer.redraw();
    }
  }, [
    map,
    markerClusterer,
    currentHeading,
    awvInfos,
    theme.typography.h5.fontStyle,
    theme.typography.fontFamily,
  ]);

  if (!mapLoaded) {
    // not map loaded
    return null;
  }
  return (
    <>
      <MyMarkerClustererF
        gridSize={isMagnify ? 0 : undefined}
        averageCenter={true}
        onClick={(cluster) => {
          setMagnify({
            center: cluster.getCenter()!,
            bounds: cluster.getBounds()!,
            heading: map?.getHeading() ?? 0,
            onClose: () => {
              setMagnify(null);
            },
          });
        }}
        calculator={calculator}
        zoomOnClick={false}
        onLoad={(clusterer) => {
          setMarkerClusterer(clusterer);
        }}
        onUnmount={() => {
          setMarkerClusterer(undefined);
        }}
        isMagnify={isMagnify}
      >
        {(clusterer) => (
          <>
            {awvInfos?.awvInfos
              ?.filter((awv) => awv.condition !== AwvInfoConditionEnum.Offline)
              .map((awvInfo) => (
                <AwvMarkerF
                  key={awvInfo.frameTypeAndNumber}
                  awvInfo={awvInfo}
                  selected={selected?.some(
                    (i) => i === awvInfo.frameTypeAndNumber
                  )}
                  onAwvSelect={onAwvSelect}
                  heading={currentHeading}
                  clusterer={clusterer}
                />
              ))}
          </>
        )}
      </MyMarkerClustererF>
      {magnify && (
        <Magnify {...magnify}>
          <NormalEdgePolygon mapData={mapData} isMagnify={true} />
          <EdgeNode mapData={mapData} isMagnify={true} />
          <RoadClosureNode mapData={mapData} isMagnify={true} />
          <NamedNode mapData={mapData} isMagnify={true} />
          <LandmarkNode mapData={mapData} isMagnify={true} />
          <AwvMapOverlay {...props} awvInfos={awvInfos} isMagnify={true} />
        </Magnify>
      )}
    </>
  );
};

interface AwvMarker {
  awvInfo: AwvInfo;
  heading: number;
  clusterer?: Clusterer;
  selected?: boolean;
  onAwvSelect?: (awvInfo: AwvInfo) => void;
}

const AwvMarkerF: FC<AwvMarker> = ({
  awvInfo,
  heading,
  clusterer,
  selected,
  onAwvSelect,
}) => {
  const theme = useTheme();
  const map = useGoogleMap();
  const [currentSize, setCurrentSize] = useState(awvIconImageSize);
  const fontSizeRef = useRef<number>(24);
  const markerRef = useRef<google.maps.Marker>();
  const pos: google.maps.LatLngLiteral = {
    lat: awvInfo.position?.positionInfo?.latitude!,
    lng: awvInfo.position?.positionInfo?.longitude!,
  };
  let zindex = Const.zIndex.awv;
  switch (awvInfo.condition) {
    case AwvInfoConditionEnum.Error:
      zindex = zindex + 30;
      break;
    case AwvInfoConditionEnum.Stop:
    case AwvInfoConditionEnum.Charging:
    case AwvInfoConditionEnum.Ota:
      zindex = zindex + 20;
      break;
    case AwvInfoConditionEnum.Running:
      zindex = zindex + 10;
      break;
  }
  zindex = zindex + parseInt(awvInfo.nameId ?? "00");
  // OverlayView object reference
  const overlayRef = useRef<google.maps.OverlayView>();
  const onOverlayViewLoad = useCallback((v: google.maps.OverlayView) => {
    console.log("onOverlayViewLoad");
    overlayRef.current = v;
  }, []);
  // awv ancher
  const angle =
    360 -
    heading -
    (180 / Math.PI) * (awvInfo?.position?.postureInfo?.yawNed ?? 0);
  const xorg = currentSize / 2;
  const yorg = currentSize * (awvIconRearWheelPos / awvIconImageSize);
  const xh = currentSize / 2;
  const yh = currentSize / 2;
  const radianAngle = (Math.PI / 180) * angle;
  const newX =
    Math.cos(radianAngle) * (xorg - xh) -
    Math.sin(radianAngle) * (yorg - yh) +
    xh;
  const newY =
    Math.sin(radianAngle) * (xorg - xh) +
    Math.cos(radianAngle) * (yorg - yh) +
    yh;
  const offsetX = newX;
  const offsetY = newY;
  // font size
  fontSizeRef.current = 24 * (currentSize / awvIconImageSize);
  useEffect(() => {
    const newLabel = {
      fontSize: fontSizeRef.current + "px",
      fontWeight: "normal",
      fontFamily: theme.typography.fontFamily,
      text: awvInfo.nameId ?? "--",
    };
    markerRef.current?.setIcon({
      url: createAwvIconDataUrl(awvInfo, heading, currentSize),
      anchor: new google.maps.Point(offsetX, offsetY),
    });
    markerRef.current?.setLabel(newLabel);
  }, [
    awvInfo,
    heading,
    currentSize,
    offsetX,
    offsetY,
    theme.typography.fontFamily,
  ]);

  /**
   * Convert meter distance to pixel distance
   * @param {number} distanceInMeter distance(m)
   * @return {number}  distance(px)
   */
  const toPixelDistance = useCallback(
    (distanceInMeter: number) => {
      // Get the area of the displayed map
      const mapBounds = map?.getBounds();
      if (!mapBounds) {
        return 0;
      }

      // Calculate the diagonal distance (m) of a region
      const boundDistanceInMeter =
        google.maps.geometry.spherical.computeDistanceBetween(
          mapBounds.getNorthEast(),
          mapBounds.getSouthWest()
        );

      // Calculate the diagonal distance (px) of a region
      const pixelNorthEast = toPixelCoord(mapBounds.getNorthEast());
      const pixelSouthWest = toPixelCoord(mapBounds.getSouthWest());
      const boundDistanceInPixel = Math.sqrt(
        Math.pow(pixelNorthEast.x - pixelSouthWest.x, 2) +
          Math.pow(pixelNorthEast.y - pixelSouthWest.y, 2)
      );

      // Calculate the target pixel distance from the number of pixels per meter
      const ratio = boundDistanceInPixel / boundDistanceInMeter;
      return distanceInMeter * ratio;
    },
    [map]
  );

  const updateIconSize = useCallback(() => {
    setTimeout(() => {
      // Calculate AWV body size in pixel
      const awvPxSize = toPixelDistance(Const.awvSpecSize);
      console.log("AWW SIZE:", awvPxSize);
      const newSize =
        awvPxSize < Const.awvIconMinSize ? Const.awvIconMinSize : awvPxSize;

      // Correct AWV body size to image size
      setCurrentSize(newSize * (awvIconImageSize / awvIconVehicleLength));
    }, Const.awvIconSizeUpdateWait);
  }, [toPixelDistance]);

  useEffect(() => {
    updateIconSize();
    if (map) {
      const zoomChangeListener = map.addListener("zoom_changed", () => {
        const zoomLevel = map.getZoom();
        console.log("Zoom level changed:", zoomLevel);
        if (zoomLevel !== 0) {
          updateIconSize();
        }
      });
      return () => {
        google.maps.event.removeListener(zoomChangeListener);
      };
    }
  }, [map, updateIconSize]);

  /**
   * Convert latitude/longitude to pixel coordinates
   * @param {google.maps.LatLngLiteral | google.maps.LatLng} target Target coordinates (latitude/longitude)
   * @return {Object}  coordinates (pixels)
   */
  const toPixelCoord = (
    target: google.maps.LatLngLiteral | google.maps.LatLng
  ) => {
    console.info("toPixelCoord");
    const projection = overlayRef.current?.getProjection();

    // Origin of svg = top left of map, so direct conversion is possible
    return (
      projection?.fromLatLngToContainerPixel(target) ??
      new google.maps.Point(0, 0)
    );
  };

  return (
    <OverlayViewF
      key={"overlay-awv-" + awvInfo.frameTypeAndNumber}
      position={pos}
      getPixelPositionOffset={(w, h) => {
        return { x: -(w / 2), y: -(h / 2) };
      }}
      mapPaneName="overlayMouseTarget"
      zIndex={Const.zIndex.highlight}
      onLoad={onOverlayViewLoad}
    >
      <MarkerF
        position={pos}
        icon={{
          url: createAwvIconDataUrl(awvInfo, heading, currentSize),
          anchor: new google.maps.Point(offsetX, offsetY),
        }}
        label={{
          fontSize: fontSizeRef.current + "px",
          fontWeight: "normal",
          fontFamily: theme.typography.fontFamily,
          text: awvInfo.nameId ?? "--",
        }}
        zIndex={zindex}
        onClick={() => {
          onAwvSelect?.(awvInfo);
        }}
        onLoad={(marker: google.maps.Marker) => {
          markerRef.current = marker;
        }}
        onUnmount={() => {
          markerRef.current = undefined;
        }}
        clusterer={clusterer}
      >
        {selected && (
          <OverlayViewF
            key={"overlay-highlight-" + awvInfo.frameTypeAndNumber}
            position={pos}
            getPixelPositionOffset={(w, h) => {
              return { x: -(w / 2), y: -(h / 2) };
            }}
            mapPaneName="overlayMouseTarget"
            zIndex={Const.zIndex.highlight}
          >
            <HighLightIcon />
          </OverlayViewF>
        )}
      </MarkerF>
    </OverlayViewF>
  );
};
